import { Printer, ArrowLeft, BookOpen, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import api from "../../api";

/**
 * RubricHandbook — the single source of truth for VI L&D leadership on
 * every evaluation parameter used across the certification. Print-optimised
 * (see /app/frontend/src/index.css @media print rules) so a "Download as PDF"
 * click produces a clean 2-3 page handout ready to share.
 *
 * The download uses a server-side Playwright render (GET /admin/rubric/pdf)
 * so the resulting PDF has NO browser URL / date / page-number in the
 * header or footer — clean handout ready for external sharing.
 */
export default function RubricHandbook({ embedded = false }) {
  const [busy, setBusy] = useState(false);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/rubric/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vi-sales-cert-rubric-handbook.pdf";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
    } catch (e) {
      // Fallback — server-side PDF failed (Playwright missing browsers,
      // network issue, etc). Fall back to window.print() so the user still
      // gets a printable version, with the caveat that Chrome will add its
      // default URL / date footer unless they uncheck "Headers and footers".
      window.print();
    } finally { setBusy(false); }
  };

  return (
    <div className={embedded ? "" : "max-w-4xl mx-auto p-6 space-y-8"} data-testid="rubric-handbook">
      {!embedded && (
        <div className="flex items-start justify-between gap-4 print:hidden">
          <div>
            <Link to="/admin" className="text-xs uppercase tracking-widest text-[#878787] hover:text-[#0A0A0A] flex items-center gap-1 mb-4">
              <ArrowLeft size={14} /> Back to admin
            </Link>
            <div className="overline">VI SALES CERTIFICATION 2026 · EVALUATION HANDBOOK</div>
            <h1 className="font-heading font-black text-3xl tracking-tighter mt-2">Rubric Handbook</h1>
            <p className="text-sm text-[#525252] mt-2 max-w-2xl leading-relaxed">
              Every evaluation parameter used to score MCQ, Sales Pitch and Role Play — plus the plain-English definition of what a rubric score of 1/5, 3/5 or 5/5 means. Share this with VI L&amp;D leadership before the pilot.
            </p>
          </div>
          <button
            onClick={downloadPdf}
            disabled={busy}
            className="btn btn-primary"
            data-testid="rubric-download-pdf"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
            {busy ? "Preparing PDF…" : "Download as PDF"}
          </button>
        </div>
      )}

      {/* Print header — hidden on screen, shown on the printed PDF */}
      <div className="hidden print:block mb-6">
        <div className="text-[10px] uppercase tracking-widest text-[#5F004B] font-bold">Vi Business Academy · Sales Certification 2026</div>
        <h1 className="font-heading font-black text-2xl mt-1">Rubric Handbook — Evaluation Parameters</h1>
        <div className="text-[10px] text-[#525252] mt-1">Powered by Mercury Compass Assess · Rubric version 2026-02-vi-mobile</div>
      </div>

      {/* Section 1 — MCQ */}
      <section className="space-y-3 break-inside-avoid" data-testid="rubric-mcq">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#5F004B] text-white flex items-center justify-center font-bold text-sm">1</div>
          <h2 className="font-heading font-bold text-xl">Knowledge MCQ · <span className="text-[#525252] font-normal">50 marks</span></h2>
        </div>
        <p className="text-sm text-[#0A0A0A] leading-relaxed">
          Fully <strong>objective</strong>. Ten single-correct MCQs on Vi Ascend Enterprise product knowledge — <em>Vi Business, Fixed Line, 5G Business, IoT, MDM, Vi Secure Connect,</em> pricing constructs and competitive positioning.
        </p>
        <div className="border border-[#E5E7EB] text-xs">
          <div className="grid grid-cols-3 bg-[#F5F3EE] px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#525252]">
            <div>Metric</div><div>Weight</div><div>Notes</div>
          </div>
          <div className="grid grid-cols-3 px-3 py-2 border-t border-[#E5E7EB]">
            <div className="font-semibold">Each correct answer</div><div>5 marks</div><div>10 questions × 5 = 50 marks total</div>
          </div>
          <div className="grid grid-cols-3 px-3 py-2 border-t border-[#E5E7EB]">
            <div className="font-semibold">Attempts allowed</div><div>2</div><div>Best score kept · earlier attempt wins on a tie</div>
          </div>
          <div className="grid grid-cols-3 px-3 py-2 border-t border-[#E5E7EB]">
            <div className="font-semibold">Question mix &amp; order</div><div>Random</div><div>No two candidates or attempts see the same paper</div>
          </div>
        </div>
      </section>

      {/* Section 2 — Sales Pitch */}
      <section className="space-y-3 break-inside-avoid" data-testid="rubric-pitch">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#5F004B] text-white flex items-center justify-center font-bold text-sm">2</div>
          <h2 className="font-heading font-bold text-xl">Sales Pitch · <span className="text-[#525252] font-normal">25 marks</span></h2>
        </div>
        <p className="text-sm text-[#0A0A0A] leading-relaxed">
          One continuous 5-minute pitch, scored by <strong>Claude Sonnet 4.5</strong> against a <strong>per-prompt rubric with 3 anchors</strong>. The rubric lives on each pitch prompt so trainers can author scenario-specific criteria.
        </p>
        <div className="border border-[#E5E7EB] text-xs">
          <div className="grid grid-cols-[100px_1fr] bg-[#F5F3EE] px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#525252]">
            <div>Anchor</div><div>What the candidate demonstrated</div>
          </div>
          <div className="grid grid-cols-[100px_1fr] px-3 py-2 border-t border-[#E5E7EB]">
            <div className="font-bold text-[#059669]">5 · Target</div>
            <div>Full structured pitch: <strong>hook → discovery → solution alignment → commercials → close</strong>. Uses Vi Business terminology accurately. Adapts to prospect persona and pain.</div>
          </div>
          <div className="grid grid-cols-[100px_1fr] px-3 py-2 border-t border-[#E5E7EB]">
            <div className="font-bold text-[#F59E0B]">3 · Proficient</div>
            <div>Structured but partial: 3-4 of the 5 pillars covered. Basic product references. Some hesitation or filler.</div>
          </div>
          <div className="grid grid-cols-[100px_1fr] px-3 py-2 border-t border-[#E5E7EB]">
            <div className="font-bold text-[#EE2737]">1 · Anti-pattern</div>
            <div>Reading verbatim from a script. Monotone delivery. Missing commercial construct. Product name errors.</div>
          </div>
        </div>
        <p className="text-[11px] text-[#525252]">
          Marks conversion: rubric score × 5 = section marks · <em>1→5, 2→10, 3→15, 4→20, 5→25</em>
        </p>
      </section>

      {/* Section 3 — Role Play */}
      <section className="space-y-3 break-inside-avoid" data-testid="rubric-roleplay">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#5F004B] text-white flex items-center justify-center font-bold text-sm">3</div>
          <h2 className="font-heading font-bold text-xl">AI Role Play · <span className="text-[#525252] font-normal">25 marks</span></h2>
        </div>
        <p className="text-sm text-[#0A0A0A] leading-relaxed">
          5-minute voice conversation with an AI-simulated buyer persona (CIO, Procurement Lead, SME founder), scored by <strong>Claude Sonnet 4.5</strong> against a <strong>per-scenario BARS rubric</strong> for the target competency.
        </p>
        <div className="space-y-1">
          <div className="font-heading font-bold text-sm">Competencies evaluated (per scenario):</div>
          <ul className="text-sm text-[#0A0A0A] pl-5 list-disc marker:text-[#EE2737] space-y-1">
            <li><strong>Consultative discovery</strong> — did they diagnose before prescribing?</li>
            <li><strong>Solution framing</strong> — did they connect Ascend Enterprise features to the buyer&apos;s <em>stated</em> pain?</li>
            <li><strong>Commercial construct</strong> — did they land tiered pricing or TCO?</li>
            <li><strong>Objection handling</strong> — did they reframe rather than defend?</li>
            <li><strong>Closing / next-step</strong> — did they secure a defined next action?</li>
          </ul>
        </div>
        <p className="text-[11px] text-[#525252]">
          Marks conversion: BARS score × 5 = section marks
        </p>
      </section>

      {/* BARS scale */}
      <section className="space-y-3 break-inside-avoid" data-testid="rubric-bars">
        <h2 className="font-heading font-black text-2xl">What &quot;Rubric Score 1/5&quot; means</h2>
        <p className="text-sm text-[#0A0A0A] leading-relaxed">
          <strong>BARS = Behaviourally Anchored Rating Scale.</strong> Each score is tied to <em>observable behaviour</em>, not a subjective impression. The AI must attach a one-sentence transcript-anchored justification to every rubric score — that&apos;s what makes 1/5 defensible, not opinion.
        </p>
        <div className="border border-[#E5E7EB] text-xs">
          <div className="grid grid-cols-[60px_120px_1fr] bg-[#F5F3EE] px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#525252]">
            <div>Score</div><div>Label</div><div>What the candidate demonstrated</div>
          </div>
          {[
            { s: "5", label: "Expert", color: "#059669", body: "Consistently applied the target behaviour with nuance. Handles multi-layered objections, uses buyer's own language, closes with a concrete next step." },
            { s: "4", label: "Advanced", color: "#059669", body: "Applied the behaviour well with minor gaps. Structure and product knowledge solid; may have missed one commercial lever." },
            { s: "3", label: "Proficient", color: "#F59E0B", body: "Right direction but partial. Covered 3 of 5 pillars; adequate structure; some depth missing." },
            { s: "2", label: "Developing", color: "#F59E0B", body: "Attempted the behaviour but with visible gaps. Discovery skipped, or solution not tied to stated pain, or commercials vague." },
            { s: "1", label: "Foundational", color: "#EE2737", body: "Fell into the anti-pattern. Product-pitching without discovery, reading verbatim, or misusing terminology." },
          ].map((row) => (
            <div key={row.s} className="grid grid-cols-[60px_120px_1fr] px-3 py-2 border-t border-[#E5E7EB]">
              <div className="font-black text-lg" style={{ color: row.color }}>{row.s}</div>
              <div className="font-bold self-center" style={{ color: row.color }}>{row.label}</div>
              <div className="self-center leading-relaxed">{row.body}</div>
            </div>
          ))}
        </div>
        <div className="bg-[#FEF2F2] border-l-4 border-[#EE2737] p-3 text-xs text-[#0A0A0A]">
          <strong className="text-[#7F1D1D]">Example — a candidate scored 1/5 on &quot;Consultative Discovery&quot;:</strong><br />
          <em>&quot;The candidate opened with a product-first pitch (Vi 5G Business features) without any discovery question. They quoted commercials by turn 3 despite the buyer stating a broader business context they wanted to discuss first. This matches the &apos;Foundational&apos; anchor — pitching without diagnosis.&quot;</em>
        </div>
      </section>

      {/* Aggregate + certification */}
      <section className="space-y-3 break-inside-avoid" data-testid="rubric-aggregate">
        <h2 className="font-heading font-bold text-xl">Aggregate &amp; certification</h2>
        <div className="border border-[#E5E7EB] text-xs">
          <div className="grid grid-cols-[1fr_120px] bg-[#F5F3EE] px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#525252]">
            <div>Section</div><div className="text-right">Weight</div>
          </div>
          <div className="grid grid-cols-[1fr_120px] px-3 py-2 border-t border-[#E5E7EB]"><div>Knowledge MCQ</div><div className="text-right">50 marks</div></div>
          <div className="grid grid-cols-[1fr_120px] px-3 py-2 border-t border-[#E5E7EB]"><div>Sales Pitch</div><div className="text-right">25 marks</div></div>
          <div className="grid grid-cols-[1fr_120px] px-3 py-2 border-t border-[#E5E7EB]"><div>AI Role Play</div><div className="text-right">25 marks</div></div>
          <div className="grid grid-cols-[1fr_120px] px-3 py-2 border-t border-[#E5E7EB] bg-[#FEF2F2]"><div className="font-bold">Total</div><div className="text-right font-bold">100 marks</div></div>
        </div>
        <p className="text-sm text-[#0A0A0A] leading-relaxed">
          <strong>Certification threshold: ≥ 60 aggregate marks.</strong> Section-wise minima are not enforced — a strong MCQ can offset a weaker Pitch, and vice versa. Integrity events (voice-match failures, screenshot suspicion, multiple faces detected) do not automatically fail the candidate but are surfaced in the admin Integrity Center for human review before results release.
        </p>
      </section>

      {/* Footer for print */}
      <div className="hidden print:block mt-8 pt-4 border-t border-[#E5E7EB] text-[9px] text-[#878787] text-center">
        Vi Business Academy · Powered by Mercury Compass Assess · Rubric v2026-02-vi-mobile · Generated {new Date().toLocaleDateString()}
      </div>
    </div>
  );
}
